import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sword, ChevronDown, ChevronRight, Save } from "lucide-react";
import { toast } from "sonner";

const CODES = ["fioretto", "spada", "sciabola"];
const STATUSES = [
  { v: "attiva", l: "Attiva" },
  { v: "secondaria", l: "Secondaria" },
  { v: "sospesa", l: "Sospesa" },
  { v: "conclusa", l: "Conclusa" },
];

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * Base mode: 3 large checkboxes + primary radio.
 * "Dettagli" panel exposes priority/dates/status/notes.
 */
export default function AthleteWeaponsPanel({ athleteId, canEdit = true, onSaved }) {
  const [items, setItems] = useState({});     // code -> row {selected, is_primary, ...}
  const [loading, setLoading] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    if (!athleteId) return;
    api.get(`/athletes/${athleteId}/weapons`).then((r) => {
      const map = {};
      CODES.forEach((c) => { map[c] = { selected: false, is_primary: false, priority_order: 1, status: "attiva", start_date: "", end_date: "", notes: "" }; });
      (r.data || []).forEach((w) => {
        map[w.weapon_code] = {
          selected: w.status !== "conclusa",
          is_primary: !!w.is_primary,
          priority_order: w.priority_order || 1,
          status: w.status || "attiva",
          start_date: w.start_date || "",
          end_date: w.end_date || "",
          notes: w.notes || "",
        };
      });
      setItems(map);
    });
  }, [athleteId]);

  const toggle = (code) => {
    if (!canEdit) return;
    setItems((prev) => {
      const next = { ...prev };
      next[code] = { ...next[code], selected: !next[code].selected };
      // If un-selecting primary, clear is_primary
      if (!next[code].selected) next[code].is_primary = false;
      // Ensure at least one primary if any selected
      const selectedCodes = Object.keys(next).filter((c) => next[c].selected);
      if (selectedCodes.length > 0 && !selectedCodes.some((c) => next[c].is_primary)) {
        next[selectedCodes[0]].is_primary = true;
      }
      return next;
    });
  };

  const setPrimary = (code) => {
    if (!canEdit) return;
    setItems((prev) => {
      const next = { ...prev };
      CODES.forEach((c) => { next[c] = { ...next[c], is_primary: c === code }; });
      // Auto-select if primary chosen
      next[code].selected = true;
      return next;
    });
  };

  const setField = (code, field, value) => {
    setItems((prev) => ({ ...prev, [code]: { ...prev[code], [field]: value } }));
  };

  const save = async () => {
    const payload = CODES
      .filter((c) => items[c]?.selected)
      .map((c) => ({
        weapon_code: c,
        is_primary: !!items[c].is_primary,
        priority_order: parseInt(items[c].priority_order || 1),
        status: items[c].status || "attiva",
        start_date: items[c].start_date || null,
        end_date: items[c].end_date || null,
        notes: items[c].notes || null,
      }));
    if (payload.length === 0 && !window.confirm("Nessuna arma selezionata: l'atleta risulterà \"Arma non impostata\". Confermi?")) return;
    if (payload.length && !payload.some((p) => p.is_primary)) payload[0].is_primary = true;
    setLoading(true);
    try {
      await api.put(`/athletes/${athleteId}/weapons`, { weapons: payload });
      toast.success("Armi salvate");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-md p-5 max-w-3xl space-y-4" data-testid="athlete-weapons-panel">
      <div className="flex items-center gap-2">
        <Sword className="w-5 h-5 text-[#0052FF]" />
        <div>
          <div className="font-heading font-semibold">Armi praticate</div>
          <div className="text-xs text-zinc-500">Seleziona le armi. L'arma principale è indicata dal riquadro nero.</div>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {CODES.map((code) => {
          const it = items[code] || {};
          const selected = it.selected;
          const primary = it.is_primary;
          return (
            <div key={code} className="space-y-2">
              <button
                type="button"
                data-testid={`weapon-toggle-${code}`}
                disabled={!canEdit}
                onClick={() => toggle(code)}
                className={`w-full h-16 rounded-md border text-sm font-semibold flex items-center justify-center gap-2 transition-colors ${
                  selected
                    ? (primary ? "bg-black text-white border-black" : "bg-zinc-100 text-zinc-800 border-zinc-300")
                    : "bg-white text-zinc-400 border-dashed border-zinc-300 hover:border-zinc-400"
                }`}
              >
                <input type="checkbox" checked={!!selected} readOnly className="w-4 h-4 accent-black" />
                {cap(code)}
              </button>
              {selected && (
                <label className={`flex items-center gap-1.5 text-xs cursor-pointer ${canEdit ? "" : "opacity-60"}`}>
                  <input
                    type="radio"
                    name="primary-weapon"
                    data-testid={`weapon-primary-${code}`}
                    checked={!!primary}
                    disabled={!canEdit}
                    onChange={() => setPrimary(code)}
                  />
                  Principale
                </label>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={() => setShowDetails((v) => !v)}
        className="text-xs uppercase tracking-wider text-zinc-500 hover:text-black flex items-center gap-1"
        data-testid="weapons-toggle-details"
      >
        {showDetails ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        Dettagli (priorità, stato, date, note)
      </button>

      {showDetails && (
        <div className="space-y-3 pt-2 border-t border-zinc-100" data-testid="weapons-details">
          {CODES.filter((c) => items[c]?.selected).map((code) => {
            const it = items[code];
            return (
              <div key={code} className="border border-zinc-200 rounded p-3 space-y-2">
                <div className="font-semibold text-sm">{cap(code)}{it.is_primary && <span className="ml-2 text-[10px] uppercase tracking-wider bg-black text-white px-1.5 py-0.5 rounded">principale</span>}</div>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
                  <div>
                    <Label className="text-xs">Priorità (1=alta)</Label>
                    <Input type="number" min={1} max={5} value={it.priority_order} disabled={!canEdit} onChange={(e) => setField(code, "priority_order", parseInt(e.target.value || 1))} data-testid={`weapon-priority-${code}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Stato</Label>
                    <Select value={it.status} onValueChange={(v) => setField(code, "status", v)} disabled={!canEdit}>
                      <SelectTrigger data-testid={`weapon-status-${code}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Inizio</Label>
                    <Input type="date" value={it.start_date} disabled={!canEdit} onChange={(e) => setField(code, "start_date", e.target.value)} />
                  </div>
                  <div>
                    <Label className="text-xs">Fine</Label>
                    <Input type="date" value={it.end_date} disabled={!canEdit} onChange={(e) => setField(code, "end_date", e.target.value)} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Note</Label>
                  <Input value={it.notes} disabled={!canEdit} onChange={(e) => setField(code, "notes", e.target.value)} placeholder="Es. tecnico dedicato, obiettivi…" />
                </div>
              </div>
            );
          })}
          {CODES.filter((c) => items[c]?.selected).length === 0 && (
            <div className="text-sm text-zinc-500 py-2">Seleziona un'arma per gestirne i dettagli.</div>
          )}
        </div>
      )}

      {canEdit && (
        <div className="pt-2">
          <Button data-testid="weapons-save" onClick={save} disabled={loading} className="h-11 bg-black hover:bg-zinc-800">
            <Save className="w-4 h-4 mr-2" />{loading ? "Salvataggio…" : "Salva armi"}
          </Button>
        </div>
      )}
    </div>
  );
}
